<script>
import StandardScheduleTableItem from '@/models/client/schedule/standardScheduleTableItem'
import ScheduleExceptionUpdateDTO from '@/models/client/schedule/scheduleExceptionUpdateDTO'
import { mapActions, mapGetters } from 'vuex'
import DayNames from '@/constants/date/DaysOfWeekString'
import WorkScheduleUpdateDTO from '@/models/client/schedule/workScheduleUpdateDTO'
import WorkSchedule from '@/models/client/schedule/workSchedule'
import ScheduleFieldTypes from '@/constants/schedule/ScheduleFieldTypes'
import TimeFieldTypes from '@/constants/schedule/TimeFieldTypes'
import BaseMixin from '@/mixins/baseMixin.js'
import BaseModal from '@/components/general/_base-modal.vue'
import BaseButton from '@/components/general/_base-button.vue'
import VuetifyTextClass from '@/constants/core/VuetifyTextClass'
import DateFormatToken from '@/constants/date/DateFormatToken'
import dayjs from 'dayjs'
import ScheduleExceptions from './schedule-exceptions.vue'
import ScheduleTimeTable from './schedule-timetable.vue'

// TODO: Tech Excellence - exception card default image persisting across location change & save changes

export default {
  name: 'WorkScheduleContainer',

  components: {
    ScheduleExceptions,
    ScheduleTimeTable,
    BaseModal,
    BaseButton,
  },
  mixins: [BaseMixin],

  props: {
    clientId: {
      type: Number,
      required: true,
    },
    locationIds: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      activeTab: 0,
      schedule: {},
      locations: [],
      loading: false,
      saving: false,
      activeFields: {},
      modalTitle: this.$t('client.schedule.exception.addTitle'),
      addExceptionDialog: false,
      editingExceptionId: null,
      exceptionTitle: '',
      exceptionDate: null,
      h5: VuetifyTextClass.h5,
      caption: VuetifyTextClass.caption,
      isExceptionModalLoading: false,
      datePickerFormat: DateFormatToken.YYYYMMDD,
    }
  },

  computed: {
    ...mapGetters('client', ['getLocation']),

    scheduleTimes() {
      return this.schedule?.scheduleTimes || {}
    },
    activeLocationId() {
      return this.locations[this.activeTab].id
    },
    showScheduleTable() {
      return Object.keys(this.scheduleTimes).length > 0
    },
    removedDays() {
      return this.schedule?.removedDays || []
    },
    scheduleTableData() {
      if (!this.schedule?.scheduleTimes) return []

      return DayNames.map((day) => {
        const { standardBreak, standardHours } =
          this.schedule.scheduleTimes[day]
        return new StandardScheduleTableItem({
          name: day,
          start: standardHours?.startTime,
          end: standardHours?.endTime,
          breakStartTime: standardBreak?.startTime,
          breakEndTime: standardBreak?.endTime,
          removed: this.removedDays.includes(day),
          activeField: this.activeFields[day] || null,
        })
      })
    },
    headers() {
      return [
        { title: 'Day', value: 'name' },
        { title: 'Start', value: 'start' },
        { title: 'End', value: 'end' },
        { title: 'Break Start', value: 'breakStartTime' },
        { title: 'Break End', value: 'breakEndTime' },
      ]
    },
    validationError() {
      if (this.removedDays.length === DayNames.length) {
        return this.$t('client.schedule.validation.atLeastOneOpenDay')
      }

      return false
    },
    isSaveBtnDisabled() {
      const invalidRow = this.scheduleTableData.find(
        (item) => !item.removed && item.firstInvalidField
      )

      return !!this.validationError || !!invalidRow
    },
    exceptionFormData() {
      return {
        title: this.exceptionTitle,
        dateLocal: this.exceptionDate,
        id: this.editingExceptionId,
      }
    },
    isShortScreen() {
      return window.innerHeight < 700
    },
  },

  watch: {
    async activeTab() {
      await this.init()
    },
  },
  async mounted() {
    await this.init()
  },

  methods: {
    ...mapActions('client', [
      'pokeLocation',
      'updateWorkSchedule',
      'getWorkSchedule',
      'upsertScheduleException',
      'deleteScheduleException',
    ]),

    reset() {
      this.schedule = null
    },

    async init() {
      this.loading = true
      this.reset()

      if (this.locationIds.length === 0 || this.clientId < 0) {
        this.setErrorState()
        return
      }

      await Promise.all(this.locationIds.map((id) => this.pokeLocation(id)))

      await this.waitForLocations(this.locationIds)

      this.locations = this.locationIds
        .map((id) => this.getLocation(id))
        .filter((location) => location.id > 0)

      const { data, errors } = await this.getWorkSchedule({
        clientId: this.clientId,
        locationId: this.activeLocationId,
      })

      if (errors.length > 0) {
        this.setErrorState()
        return
      }

      this.schedule = new WorkSchedule(data)

      this.loading = false
    },
    setErrorState() {
      this.loading = false
      this.schedule = null
    },

    async waitForLocations(ids, timeout = 5000) {
      const interval = 50
      const maxAttempts = Math.ceil(timeout / interval)

      const allLoaded = () => ids.every((id) => this.getLocation(id).id > 0)

      for (let attempt = 0; attempt <= maxAttempts; attempt++) {
        if (allLoaded()) return
        await new Promise((resolve) => setTimeout(resolve, interval))
      }
      throw new Error('Timeout waiting for locations')
    },

    // ===== Save Schedule UPSERT =====
    async saveSchedule() {
      if (!this.$refs.scheduleTable.validate()) return

      this.saving = true

      const payload = new WorkScheduleUpdateDTO(
        this.activeLocationId,
        this.scheduleTimes,
        this.removedDays
      )

      if (payload.isValid) {
        const { errors } = await this.updateWorkSchedule(payload)

        if (errors.length > 0) {
          this.saving = false
          return
        }

        await this.init()
      }
      this.saving = false
    },

    // ===== Schedule Update Handlers =====
    updateScheduleTime({ day, field, value }) {
      this.$set(this.activeFields, day, field)

      const { startTime, endTime } = TimeFieldTypes
      const { standardBreak, standardHours } = ScheduleFieldTypes

      const fieldType = field.toLowerCase()
      const timeField = fieldType.includes('start') ? startTime : endTime
      const period = fieldType.includes('break') ? standardBreak : standardHours

      const scheduleData = this.scheduleTimes[day]?.[period]

      if (!scheduleData) return

      this.$set(scheduleData, timeField, value)

      this.$nextTick(() => this.$refs.scheduleTable.validate())
    },

    openExceptionModal(id) {
      const exception = this.schedule.getException(id)

      this.modalTitle = id
        ? this.$t('client.schedule.exception.editTitle')
        : this.$t('client.schedule.exception.addTitle')
      this.editingExceptionId = exception?.id || null
      this.exceptionTitle = exception?.title || ''
      this.exceptionDate =
        exception?.dateString || dayjs().format(this.datePickerFormat)
      this.addExceptionDialog = true

      this.$nextTick(() => {
        if (this.$refs.exceptionForm) {
          this.$refs.exceptionForm.resetValidation()
        }
      })
    },
    async handleUpsertException() {
      if (!this.exceptionTitle || !this.exceptionDate) return

      const payload = new ScheduleExceptionUpdateDTO(this.exceptionFormData)

      if (payload.isValid) {
        this.isExceptionModalLoading = true
        const { data } = await this.upsertScheduleException({
          clientId: this.clientId,
          locationId: this.activeLocationId,
          exception: payload,
        })

        this.isExceptionModalLoading = false

        if (data?.id) {
          this.schedule.upsertException({
            id: data.id,
            title: this.exceptionTitle,
            dateLocal: this.exceptionDate,
          })
        }
      }

      this.addExceptionDialog = false
    },
  },
}
</script>

<template>
  <v-card
    :class="[
      { 'rounded-xl custom-elevation': isEmbedded },
      isLargeOrHigher ? 'schedule-width-lg' : 'schedule-width',
    ]"
    class="ma-0 mx-auto pa-4"
    flat
  >
    <v-card-title class="d-flex justify-space-between align-center">
      <div>
        <v-tabs
          v-model="activeTab"
          background-color="transparent"
          :color="isDarkMode ? 'warning' : 'secondary'"
          center-active
        >
          <v-tab v-for="location in locations" :key="location.id" class="tabs">
            {{ location.values.LocationName }}
          </v-tab>
        </v-tabs>
      </div>

      <v-btn
        v-if="schedule"
        elevation="0"
        :color="isDarkMode ? 'warning' : 'secondary'"
        :loading="saving"
        :disabled="isSaveBtnDisabled"
        @click="saveSchedule"
      >
        {{ $t('general.saveChanges') }}
      </v-btn>
    </v-card-title>

    <transition name="fade-down" mode="out-in">
      <div v-if="loading" :key="'skeleton-loader'">
        <div
          class="d-flex overflow-x-hidden py-6 px-6 mx-4"
          :class="isDarkMode ? 'grey lighten-1' : 'grey lighten-4'"
        >
          <v-skeleton-loader
            v-for="idx in 10"
            :key="`skeleton-exception-${idx}`"
            type="image"
            class="skeleton-schedule-card"
            :class="{ 'mr-4': idx !== 0 }"
            min-width="320"
            height="120"
            tile
          />
        </div>

        <v-card class="mt-12 mx-4 pa-6" outlined>
          <v-skeleton-loader
            type="table"
            width="100%"
            height="700px"
            class="table-thead table-tbody"
          />
        </v-card>
      </div>

      <div v-else-if="showScheduleTable" :key="'schedule'" class="px-4">
        <div
          class="pa-4 rounded-lg"
          :class="isDarkMode ? 'grey lighten-1' : 'grey lighten-4'"
        >
          <ScheduleExceptions
            :location-id="activeLocationId"
            :client-id="clientId"
            :schedule="schedule"
            @open-upsert-exception="openExceptionModal"
            @remove-exception="(id) => schedule.removeException(id)"
          />
        </div>
        <div class="rounded-lg">
          <ScheduleTimeTable
            ref="scheduleTable"
            class="mt-4"
            :schedule-data="scheduleTableData"
            :headers="headers"
            :validation-error="validationError"
            @update:times="updateScheduleTime"
            @update:removed="
              (updatedTableItems) =>
                schedule.updateRemovedDays(updatedTableItems)
            "
          />
        </div>
      </div>
      <div
        v-else-if="!loading && !schedule"
        :key="'error'"
        class="d-flex flex-column align-center justify-center my-10"
      >
        <v-icon size="80" color="error">mdi-close-circle-outline</v-icon>

        <p class="text-h6 mt-4 text-center schedule-failed-heading">
          {{ $t('client.schedule.retryPrompt.heading') }}
        </p>
        <v-btn class="mt-4 px-12 text-body-1" outlined @click="$router.go(0)">
          {{ $t('general.retry') }}
        </v-btn>
      </div>
    </transition>
    <BaseModal
      v-model="addExceptionDialog"
      width="400"
      hide-header
      hide-cancel
      :tile-actions="isShortScreen"
      :hide-actions-divider="isShortScreen"
      :title="$t('client.schedule.exception.addTitle')"
    >
      <v-form
        ref="exceptionForm"
        class="d-flex flex-column align-center pt-4"
        @submit.prevent
      >
        <p v-if="!isShortScreen" :class="[h5, isExtraLarge ? 'my-8' : 'my-4']">
          <v-icon
            class="mb-1 mr-1"
            color="secondary"
            :size="isExtraLarge ? 32 : 28"
            >mdi-calendar-plus</v-icon
          >
          {{ modalTitle }}</p
        >
        <v-text-field
          v-model="exceptionTitle"
          :class="[isShortScreen ? 'mb-6 w-80' : 'w-100 mb-1']"
          :label="isShortScreen ? modalTitle : 'Title'"
          color="secondary"
          outlined
          :dense="isShortScreen"
          :hide-details="isShortScreen"
          :rules="[(v) => !!v || 'Title is required']"
        />
        <v-date-picker
          v-model="exceptionDate"
          color="secondary"
          class="w-100"
          :no-title="isShortScreen"
          :min="new Date().toISOString().substring(0, 10)"
          :rules="[(v) => !!v || 'Date is required']"
        />
      </v-form>
      <template v-slot:actions>
        <div class="d-flex justify-space-around w-100">
          <BaseButton
            secondary
            class="px-12"
            :border-radius="isShortScreen ? '0px' : null"
            :class="{ 'flex-grow-1 px-0': isShortScreen }"
            @click="addExceptionDialog = false"
          >
            {{ $t('general.cancel') }}
          </BaseButton>
          <BaseButton
            primary
            color="secondary"
            class="px-12"
            :class="{ 'flex-grow-1 px-0': isShortScreen }"
            :border-radius="isShortScreen ? '0px' : null"
            :loading="isExceptionModalLoading"
            :disabled="!exceptionTitle || !exceptionDate"
            @click="handleUpsertException"
          >
            {{ $t('general.submit') }}
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </v-card>
</template>

<style scoped lang="scss">
@import '@/scss/_vue-transitions.scss';

.tabs {
  max-width: max-content !important;
}

.schedule-width {
  max-width: 1400px;
}
.schedule-width-lg {
  max-width: 1800px;
}

.schedule-failed-heading {
  max-width: 300px;
}

.skeleton-schedule-card {
  min-width: 320px;
  height: 120px;
  background-repeat: no-repeat;
  background-position: bottom;
  background-size: cover;
  border-radius: 12px !important;
}

.table-thead ::v-deep .v-skeleton-loader__table-heading {
  display: none;
}

.table-thead ::v-deep .v-skeleton-loader__table-thead {
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 100px !important;
  min-height: 100px !important;
}

.table-tbody ::v-deep .v-skeleton-loader__table-row {
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 100px !important;
  min-height: 100px !important;
}

.table-tbody ::v-deep .v-skeleton-loader__text {
  height: 20px !important;
  margin: 0 auto !important;
}

.dense-modal-actions ::v-deep .v-card__actions {
  padding-top: 0 !important;
}
</style>
